import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Button, Input, Select } from "@repo/o37-ui";
import {
  Search,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  CreditCard,
  RefreshCw,
  Keyboard,
  ArrowUp,
  ArrowDown,
  Building2,
  Tag,
  ArrowLeftRight,
  Link2
} from "lucide-react";
import { HoldingsTransaction, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type DateRange = 'all' | '7d' | '30d' | '90d' | 'ytd' | '1y';
type AmountFilter = 'all' | 'income' | 'expense';

interface BankAccount {
  id: string;
  name: string;
  bankName: string;
}

interface Company {
  id: string;
  name: string;
}

interface ExpenseCategory {
  id: string;
  name: string;
  shortcut_key: string;
  color: string;
  parent_id: string | null;
  children?: ExpenseCategory[];
}

interface RevenueCategory {
  id: string;
  key: string;
  name: string;
  shortcut_key: string;
  color: string;
}

interface ExtendedTransaction extends HoldingsTransaction {
  companyId?: string;
  companyName?: string;
  expenseCategoryId?: string;
  expenseCategoryName?: string;
  incomeCategoryId?: string;
  incomeCategoryName?: string;
  revenueCategory?: string;
  matchedForecastId?: string;
  isInternalTransfer?: boolean;
  transferLinkedId?: string;
}

export default function TransactionList() {
  const [transactions, setTransactions] = useState<ExtendedTransaction[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [revenueCategories, setRevenueCategories] = useState<RevenueCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [amountFilter, setAmountFilter] = useState<AmountFilter>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  // Focus mode state
  const [focusMode, setFocusMode] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [categoryMode, setCategoryMode] = useState<{ parentKey: string; parent: ExpenseCategory } | null>(null);
  const [revenueMode, setRevenueMode] = useState<{ companyId: string; companyName: string } | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-match state
  const [autoMatching, setAutoMatching] = useState(false);
  const [matchResult, setMatchResult] = useState<{ transfers: number; subscriptions: number } | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [txnRes, accountsRes, companiesRes, categoriesRes, revCategoriesRes] = await Promise.all([
        fetch(`${API_URL}/holdings/transactions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/bank-accounts`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/companies`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/expense-categories`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/revenue-categories`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (txnRes.ok) {
        const data = await txnRes.json();
        setTransactions(data.map((t: any) => ({
          id: t.id,
          importId: t.import_id,
          bankAccountId: t.bank_account_id,
          transactionDate: t.transaction_date,
          amountCents: t.amount_cents,
          description: t.description,
          vendorNameRaw: t.vendor_name_raw,
          vendorNameNormalized: t.vendor_name_normalized,
          category: t.category,
          isRecurring: t.is_recurring,
          matchedSubscriptionId: t.matched_subscription_id,
          matchStatus: t.match_status,
          matchConfidence: t.match_confidence,
          classification: t.classification,
          matchedSubscription: t.holdings_subscriptions ? {
            id: t.holdings_subscriptions.id,
            vendorName: t.holdings_subscriptions.vendor_name,
            costCents: t.holdings_subscriptions.cost_cents
          } : undefined,
          bankAccountName: t.holdings_bank_accounts?.name,
          companyId: t.company_id,
          companyName: t.holdings_companies?.name,
          expenseCategoryId: t.expense_category_id,
          expenseCategoryName: t.expense_category?.name,
          incomeCategoryId: t.income_category_id,
          incomeCategoryName: t.income_category?.name,
          revenueCategory: t.revenue_category,
          matchedForecastId: t.matched_forecast_id,
          isInternalTransfer: t.is_internal_transfer,
          transferLinkedId: t.transfer_linked_transaction_id
        })));
      }

      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setBankAccounts(data.map((a: any) => ({
          id: a.id,
          name: a.name,
          bankName: a.bank_name
        })));
      }

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(data.slice(0, 9)); // Max 9 companies for number keys
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setExpenseCategories(data);
      }

      if (revCategoriesRes.ok) {
        const data = await revCategoriesRes.json();
        setRevenueCategories(data.map((c: any) => ({
          id: c.id,
          key: c.key,
          name: c.name,
          shortcut_key: c.shortcut_key,
          color: c.color
        })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  // Auto-match transactions (detect internal transfers + smart match subscriptions)
  const runAutoMatch = async () => {
    setAutoMatching(true);
    setMatchResult(null);
    try {
      // Run internal transfer detection
      const transferRes = await fetch(`${API_URL}/holdings/transactions/detect-transfers`, {
        method: 'POST',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      const transferData = transferRes.ok ? await transferRes.json() : { detected: 0 };

      // Run smart matching (matches subscriptions based on vendor name, amount, etc.)
      const matchRes = await fetch(`${API_URL}/holdings/transactions/smart-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({})
      });
      const matchData = matchRes.ok ? await matchRes.json() : { matched: 0 };

      setMatchResult({
        transfers: transferData.detected || 0,
        subscriptions: matchData.matched || 0
      });

      // Reload transactions to show updated data
      await loadData();
    } catch (e) {
      console.error('Auto-match failed:', e);
    } finally {
      setAutoMatching(false);
    }
  };

  // Apply filters
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let cutoff: Date;
      if (dateRange === 'ytd') {
        cutoff = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      } else {
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
        cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      }
      filtered = filtered.filter(t => new Date(t.transactionDate) >= cutoff);
    }

    // Amount filter (income vs expense)
    if (amountFilter === 'income') {
      filtered = filtered.filter(t => t.amountCents > 0);
    } else if (amountFilter === 'expense') {
      filtered = filtered.filter(t => t.amountCents < 0);
    }

    // Bank account filter
    if (selectedAccount !== 'all') {
      filtered = filtered.filter(t => t.bankAccountId === selectedAccount);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.vendorNameRaw?.toLowerCase().includes(query) ||
        t.vendorNameNormalized?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.category?.toLowerCase().includes(query)
      );
    }

    // Sort by date descending
    filtered.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

    return filtered;
  }, [transactions, dateRange, amountFilter, selectedAccount, searchQuery]);

  // Paginated transactions
  const paginatedTransactions = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredTransactions.slice(start, start + pagination.pageSize);
  }, [filteredTransactions, pagination]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const nonTransfers = filteredTransactions.filter(t => !t.isInternalTransfer);
    const income = nonTransfers.filter(t => t.amountCents > 0).reduce((sum, t) => sum + t.amountCents, 0);
    const expenses = nonTransfers.filter(t => t.amountCents < 0).reduce((sum, t) => sum + Math.abs(t.amountCents), 0);
    const transfers = filteredTransactions.filter(t => t.isInternalTransfer).length;
    return { income, expenses, net: income - expenses, count: filteredTransactions.length, transfers };
  }, [filteredTransactions]);

  // Assign company to transaction
  const assignCompany = useCallback(async (transactionId: string, companyId: string | null) => {
    try {
      const res = await fetch(`${API_URL}/holdings/transactions/${transactionId}/assign-company`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ companyId })
      });

      if (res.ok) {
        const company = companies.find(c => c.id === companyId);
        setTransactions(prev => prev.map(t =>
          t.id === transactionId ? { ...t, companyId: companyId || undefined, companyName: company?.name } : t
        ));
        setLastAction(`Assigned to ${company?.name || 'None'}`);
        setTimeout(() => setLastAction(null), 2000);
      }
    } catch (e) {
      console.error('Failed to assign company:', e);
    }
  }, [companies, userId, userRole]);

  // Categorize transaction
  const categorizeTransaction = useCallback(async (transactionId: string, categoryId: string) => {
    try {
      const isExpense = (paginatedTransactions.find(t => t.id === transactionId)?.amountCents || 0) < 0;
      const res = await fetch(`${API_URL}/holdings/transactions/${transactionId}/categorize`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(isExpense ? { expenseCategoryId: categoryId } : { incomeCategoryId: categoryId })
      });

      if (res.ok) {
        // Find category name
        let categoryName = '';
        for (const parent of expenseCategories) {
          if (parent.id === categoryId) {
            categoryName = parent.name;
            break;
          }
          const child = parent.children?.find(c => c.id === categoryId);
          if (child) {
            categoryName = `${parent.name} > ${child.name}`;
            break;
          }
        }

        setTransactions(prev => prev.map(t =>
          t.id === transactionId
            ? { ...t, expenseCategoryId: categoryId, expenseCategoryName: categoryName }
            : t
        ));
        setLastAction(`Categorized: ${categoryName}`);
        setCategoryMode(null);
        setTimeout(() => setLastAction(null), 2000);
      }
    } catch (e) {
      console.error('Failed to categorize:', e);
    }
  }, [paginatedTransactions, expenseCategories, userId, userRole]);

  // Categorize revenue transaction
  const categorizeRevenue = useCallback(async (transactionId: string, companyId: string, categoryKey: string) => {
    try {
      const res = await fetch(`${API_URL}/holdings/transactions/${transactionId}/categorize-revenue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ companyId, category: categoryKey })
      });

      if (res.ok) {
        const data = await res.json();
        const company = companies.find(c => c.id === companyId);
        const category = revenueCategories.find(c => c.key === categoryKey);

        setTransactions(prev => prev.map(t =>
          t.id === transactionId
            ? {
                ...t,
                companyId,
                companyName: company?.name || data.holdings_companies?.name,
                revenueCategory: categoryKey,
                matchedForecastId: data.matched_forecast_id
              }
            : t
        ));
        setLastAction(`Revenue: ${company?.name} - ${category?.name}`);
        setTimeout(() => setLastAction(null), 2000);
      }
    } catch (e) {
      console.error('Failed to categorize revenue:', e);
    }
  }, [companies, revenueCategories, userId, userRole]);

  // Keyboard handler
  useEffect(() => {
    if (!focusMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const currentTx = paginatedTransactions[selectedIndex];
      if (!currentTx) return;

      // Navigation
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, paginatedTransactions.length - 1));
        setCategoryMode(null);
        setRevenueMode(null);
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        setCategoryMode(null);
        setRevenueMode(null);
        return;
      }

      // Escape to exit category mode, revenue mode, or focus mode
      if (e.key === 'Escape') {
        if (categoryMode) {
          setCategoryMode(null);
        } else if (revenueMode) {
          setRevenueMode(null);
        } else {
          setFocusMode(false);
        }
        return;
      }

      const isIncome = currentTx.amountCents > 0;

      // If in revenue mode (income transaction with company selected), handle category key
      if (revenueMode && isIncome) {
        const revCat = revenueCategories.find(
          c => c.shortcut_key?.toUpperCase() === e.key.toUpperCase()
        );
        if (revCat) {
          categorizeRevenue(currentTx.id, revenueMode.companyId, revCat.key);
          setRevenueMode(null);
        }
        return;
      }

      // Number keys for company assignment (1-9)
      if (/^[1-9]$/.test(e.key)) {
        const companyIndex = parseInt(e.key) - 1;
        if (companyIndex < companies.length) {
          const company = companies[companyIndex];
          // For income transactions, enter revenue mode to wait for category key
          if (isIncome) {
            setRevenueMode({ companyId: company.id, companyName: company.name });
            setLastAction(`Company: ${company.name} - press R/S/O/P/C/M/L/X`);
          } else {
            // For expenses, just assign company
            assignCompany(currentTx.id, company.id);
          }
        }
        return;
      }

      // 0 to clear company
      if (e.key === '0') {
        assignCompany(currentTx.id, null);
        setRevenueMode(null);
        return;
      }

      // If in category mode (expenses), handle second key
      if (categoryMode) {
        const child = categoryMode.parent.children?.find(
          c => c.shortcut_key?.toUpperCase() === e.key.toUpperCase()
        );
        if (child) {
          categorizeTransaction(currentTx.id, child.id);
        }
        return;
      }

      // QWERTY keys for parent category selection (expenses only)
      if (!isIncome) {
        const parentCat = expenseCategories.find(
          c => c.shortcut_key?.toUpperCase() === e.key.toUpperCase()
        );
        if (parentCat) {
          if (parentCat.children && parentCat.children.length > 0) {
            setCategoryMode({ parentKey: e.key.toUpperCase(), parent: parentCat });
          } else {
            categorizeTransaction(currentTx.id, parentCat.id);
          }
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusMode, selectedIndex, paginatedTransactions, companies, expenseCategories, revenueCategories, categoryMode, revenueMode, assignCompany, categorizeTransaction, categorizeRevenue]);

  // Scroll selected row into view
  useEffect(() => {
    if (focusMode && containerRef.current) {
      const row = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, focusMode]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.ceil(filteredTransactions.length / pagination.pageSize);

  return (
    <div ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Transactions</h1>
          <p className="text-neutral-500">View and categorize bank transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={runAutoMatch}
            disabled={autoMatching}
            icon={<RefreshCw className={`w-4 h-4 ${autoMatching ? 'animate-spin' : ''}`} />}
            text={autoMatching ? 'Matching...' : 'Auto-match'}
          />
          <Link to="/holdings/bank-imports/mercury">
            <Button variant="outline" icon={<Link2 className="w-4 h-4" />} text="Mercury Setup" />
          </Link>
          <Button
            variant={focusMode ? 'primary' : 'outline'}
            onClick={() => {
              setFocusMode(!focusMode);
              setSelectedIndex(0);
              setCategoryMode(null);
              setRevenueMode(null);
            }}
            icon={<Keyboard className="w-4 h-4" />}
            text={focusMode ? 'Exit Focus Mode' : 'Focus Mode'}
          />
        </div>
      </div>

      {/* Auto-match Result */}
      {matchResult && (
        <Card className="mb-4 bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-emerald-600" />
                <div className="text-emerald-800">
                  <span className="font-medium">Auto-match complete:</span>{' '}
                  {matchResult.transfers > 0 && <span>{matchResult.transfers} internal transfer(s) detected. </span>}
                  {matchResult.subscriptions > 0 && <span>{matchResult.subscriptions} subscription(s) matched. </span>}
                  {matchResult.transfers === 0 && matchResult.subscriptions === 0 && <span>No new matches found.</span>}
                </div>
              </div>
              <button onClick={() => setMatchResult(null)} className="text-emerald-600 hover:text-emerald-800">
                ✕
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Focus Mode Helper */}
      {focusMode && (
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Keyboard className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-blue-900 mb-2">Keyboard Shortcuts</div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-blue-700 font-medium mb-1">Navigate</div>
                    <div className="text-blue-600"><kbd className="px-1 py-0.5 bg-white rounded text-xs">↑</kbd> <kbd className="px-1 py-0.5 bg-white rounded text-xs">↓</kbd> Move selection</div>
                    <div className="text-blue-600"><kbd className="px-1 py-0.5 bg-white rounded text-xs">Esc</kbd> Exit</div>
                  </div>
                  <div>
                    <div className="text-blue-700 font-medium mb-1">Companies</div>
                    {companies.map((c, i) => (
                      <div key={c.id} className="text-blue-600">
                        <kbd className="px-1 py-0.5 bg-white rounded text-xs">{i + 1}</kbd> {c.name}
                      </div>
                    ))}
                    <div className="text-blue-600"><kbd className="px-1 py-0.5 bg-white rounded text-xs">0</kbd> Clear</div>
                  </div>
                  <div>
                    <div className="text-blue-700 font-medium mb-1">
                      {revenueMode ? 'Revenue Categories' : 'Expense Categories'}
                    </div>
                    {revenueMode ? (
                      <>
                        <div className="text-blue-800 font-medium">{revenueMode.companyName}:</div>
                        {revenueCategories.map(c => (
                          <div key={c.id} className="text-blue-600">
                            <kbd className="px-1 py-0.5 bg-white rounded text-xs">{c.shortcut_key}</kbd> {c.name}
                          </div>
                        ))}
                      </>
                    ) : categoryMode ? (
                      <>
                        <div className="text-blue-800 font-medium">{categoryMode.parent.name}:</div>
                        {categoryMode.parent.children?.map(c => (
                          <div key={c.id} className="text-blue-600">
                            <kbd className="px-1 py-0.5 bg-white rounded text-xs">{c.shortcut_key}</kbd> {c.name}
                          </div>
                        ))}
                      </>
                    ) : (
                      expenseCategories.map(c => (
                        <div key={c.id} className="text-blue-600">
                          <kbd className="px-1 py-0.5 bg-white rounded text-xs">{c.shortcut_key}</kbd> {c.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              {lastAction && (
                <div className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
                  {lastAction}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                <ArrowUpDown className="w-5 h-5 text-neutral-600" />
              </div>
              <div>
                <div className="text-sm text-neutral-500">Transactions</div>
                <div className="text-xl font-semibold">{stats.count.toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-sm text-neutral-500">Income</div>
                <div className="text-xl font-semibold text-emerald-600">{formatCents(stats.income)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-sm text-neutral-500">Expenses</div>
                <div className="text-xl font-semibold">{formatCents(stats.expenses)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.net >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <CreditCard className={`w-5 h-5 ${stats.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
              </div>
              <div>
                <div className="text-sm text-neutral-500">Net</div>
                <div className={`text-xl font-semibold ${stats.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stats.net >= 0 ? '+' : ''}{formatCents(stats.net)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-sm text-neutral-500">Transfers</div>
                <div className="text-xl font-semibold text-purple-600">{stats.transfers}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search transactions..."
            className="pl-9"
          />
        </div>

        <Select value={dateRange} onChange={(e) => setDateRange(e.target.value as DateRange)}>
          <option value="all">All Time</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="ytd">Year to Date</option>
          <option value="1y">Last Year</option>
        </Select>

        <Select value={amountFilter} onChange={(e) => setAmountFilter(e.target.value as AmountFilter)}>
          <option value="all">All Transactions</option>
          <option value="income">Income Only</option>
          <option value="expense">Expenses Only</option>
        </Select>

        {bankAccounts.length > 1 && (
          <Select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
            <option value="all">All Accounts</option>
            {bankAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        )}

        <Button
          variant="outline"
          onClick={loadData}
          icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
          text="Refresh"
        />
      </div>

      {/* Transaction List */}
      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-neutral-300 mx-auto mb-4 animate-spin" />
            <p className="text-neutral-500">Loading transactions...</p>
          </CardContent>
        </Card>
      ) : filteredTransactions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Transactions Found</h3>
            <p className="text-neutral-500 mb-4">
              {searchQuery || dateRange !== 'all' || amountFilter !== 'all' || selectedAccount !== 'all'
                ? 'Try adjusting your filters.'
                : 'Import bank statements or sync with Mercury to see transactions.'}
            </p>
            <Link to="/holdings/bank-imports/mercury">
              <Button text="Connect Mercury" />
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wider py-3 px-4 w-20">Date</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wider py-3 px-4">Description</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wider py-3 px-4 w-32">Category</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wider py-3 px-4 w-32">Company</th>
                  <th className="text-right text-xs font-medium text-neutral-500 uppercase tracking-wider py-3 px-4 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((tx, index) => (
                  <tr
                    key={tx.id}
                    data-index={index}
                    className={`border-b border-neutral-50 hover:bg-neutral-50 transition-colors ${
                      focusMode && index === selectedIndex ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''
                    } ${tx.isInternalTransfer ? 'opacity-60' : ''}`}
                    onClick={() => focusMode && setSelectedIndex(index)}
                  >
                    <td className="py-3 px-4">
                      <span className="text-neutral-600 text-sm">{formatDate(tx.transactionDate)}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {tx.isInternalTransfer && (
                          <ArrowLeftRight className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        )}
                        <div>
                          <div className="font-medium text-neutral-900">
                            {tx.vendorNameNormalized || tx.vendorNameRaw || 'Unknown'}
                          </div>
                          {tx.description && tx.description !== tx.vendorNameRaw && (
                            <div className="text-xs text-neutral-500 truncate max-w-xs">
                              {tx.description}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            {tx.isInternalTransfer && (
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Transfer</span>
                            )}
                            {tx.matchStatus === 'matched' && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Matched</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {tx.revenueCategory ? (
                        <span className="inline-flex items-center gap-1 text-sm px-2 py-1 bg-emerald-50 text-emerald-700 rounded">
                          <Tag className="w-3 h-3" />
                          {revenueCategories.find(c => c.key === tx.revenueCategory)?.name || tx.revenueCategory}
                          {tx.matchedForecastId && (
                            <span className="w-2 h-2 bg-emerald-500 rounded-full" title="Matched to forecast" />
                          )}
                        </span>
                      ) : tx.expenseCategoryName || tx.incomeCategoryName ? (
                        <span className="inline-flex items-center gap-1 text-sm px-2 py-1 bg-neutral-100 rounded">
                          <Tag className="w-3 h-3" />
                          {tx.expenseCategoryName || tx.incomeCategoryName}
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {tx.companyName ? (
                        <span className="inline-flex items-center gap-1 text-sm px-2 py-1 bg-blue-50 text-blue-700 rounded">
                          <Building2 className="w-3 h-3" />
                          {tx.companyName}
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-medium ${tx.amountCents > 0 ? 'text-emerald-600' : 'text-neutral-900'}`}>
                        {tx.amountCents > 0 ? '+' : ''}{formatCents(tx.amountCents)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100">
            <div className="text-sm text-neutral-500">
              Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredTransactions.length)} of{' '}
              {filteredTransactions.length} transactions
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }))}
                disabled={pagination.pageIndex === 0}
                icon={<ArrowUp className="w-4 h-4 rotate-[-90deg]" />}
                text="Previous"
              />
              <span className="text-sm text-neutral-600">
                Page {pagination.pageIndex + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPagination(p => ({ ...p, pageIndex: p.pageIndex + 1 }))}
                disabled={pagination.pageIndex >= totalPages - 1}
                icon={<ArrowDown className="w-4 h-4 rotate-[-90deg]" />}
                text="Next"
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
